import React, { useState } from "react";
import styled from "styled-components";

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1em;
  color: #fff;
  box-sizing: border-box;
`;

const DfbIdBox = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background-color: #181d25;
  border-radius: 4px;
  padding: 1em;
  margin-bottom: 1em;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
`;

const Label = styled.div`
  font-size: 0.8em;
  color: #8a94a6;
  margin-bottom: 0.25em;
`;

const DfbIdValue = styled.div`
  font-size: 1.5em;
  font-family: monospace;
  user-select: all;
`;

const TreeContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #0e1219;
  border-radius: 4px;
  padding: 1em;
  font-family: monospace;
  font-size: 0.85em;

  &&::-webkit-scrollbar {
    width: 12px;
  }
  &&::-webkit-scrollbar-track {
    background-color: #181d25;
  }
  &&::-webkit-scrollbar-thumb {
    background-color: #384454;
    border-radius: 10px;
    border: 2px solid #181d25;
    background-clip: padding-box;
  }
  &&::-webkit-scrollbar-thumb:hover {
    background-color: #db0a41;
  }
`;

const Line = styled.div`
  display: flex;
  align-items: flex-start;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ToggleableLine = styled(Line)`
  cursor: pointer;
  user-select: none;
  :hover {
    background-color: #181d25;
  }
`;

const Toggle = styled.span`
  display: inline-block;
  width: 1em;
  flex-shrink: 0;
  color: #8a94a6;
`;

const Indent = styled.div`
  margin-left: 1.25em;
`;

const KeyName = styled.span`
  color: #6ab0f3;
  margin-right: 0.35em;
`;

const StringValue = styled.span`
  color: #b5cea8;
`;

const NumberValue = styled.span`
  color: #d19a66;
`;

const BooleanValue = styled.span`
  color: #db0a41;
`;

const NullValue = styled.span`
  color: #8a94a6;
`;

const Bracket = styled.span`
  color: #8a94a6;
`;

function PrimitiveValue({ value }) {
  if (value === null) return <NullValue>null</NullValue>;
  if (value === undefined) return <NullValue>undefined</NullValue>;
  if (typeof value === "string") return <StringValue>"{value}"</StringValue>;
  if (typeof value === "number") return <NumberValue>{value}</NumberValue>;
  if (typeof value === "boolean")
    return <BooleanValue>{String(value)}</BooleanValue>;
  return <span>{String(value)}</span>;
}

function JsonNode({ keyName, value }) {
  const [open, setOpen] = useState(true);
  const isObject = value !== null && typeof value === "object";

  if (!isObject) {
    return (
      <Line>
        {keyName !== null && <KeyName>{keyName}:</KeyName>}
        <PrimitiveValue value={value} />
      </Line>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? value.map((v, i) => [i, v])
    : Object.entries(value);
  const isEmpty = entries.length === 0;
  const openBracket = isArray ? "[" : "{";
  const closeBracket = isArray ? "]" : "}";

  if (isEmpty) {
    return (
      <Line>
        {keyName !== null && <KeyName>{keyName}:</KeyName>}
        <Bracket>
          {openBracket}
          {closeBracket}
        </Bracket>
      </Line>
    );
  }

  return (
    <div>
      <ToggleableLine onClick={() => setOpen(!open)}>
        <Toggle>{open ? "▼" : "▶"}</Toggle>
        {keyName !== null && <KeyName>{keyName}:</KeyName>}
        <Bracket>
          {openBracket}
          {!open && ` … ${entries.length} ${isArray ? "items" : "keys"} `}
          {!open && closeBracket}
        </Bracket>
      </ToggleableLine>
      {open && (
        <>
          <Indent>
            {entries.map(([k, v]) => (
              <JsonNode key={k} keyName={isArray ? null : k} value={v} />
            ))}
          </Indent>
          <Line>
            <Bracket>{closeBracket}</Bracket>
          </Line>
        </>
      )}
    </div>
  );
}

export default function DebugDFB({ game }) {
  return (
    <Main>
      <DfbIdBox>
        <Label>DFB Match ID (game.dfb_ID)</Label>
        <DfbIdValue>{game?.dfb_ID || "N/A"}</DfbIdValue>
      </DfbIdBox>
      <TreeContainer>
        <JsonNode keyName={null} value={game} />
      </TreeContainer>
    </Main>
  );
}
